import { useCallback, useEffect, useState } from 'react';

// Simple client-side credit tracker for Metis-powered actions.
// New users start with STARTING_CREDITS; each AI action costs 1 credit.
// This is intentionally client-side / MOCKED until live billing is wired up.

const KEY = 'studyfolio.credits.v1';
const STARTING_CREDITS = 20;
const LOW_THRESHOLD = 5;

function read() {
    try {
        const raw = localStorage.getItem(KEY);
        if (raw === null) {
            localStorage.setItem(KEY, String(STARTING_CREDITS));
            return STARTING_CREDITS;
        }
        const n = parseInt(raw, 10);
        return Number.isFinite(n) ? n : STARTING_CREDITS;
    } catch {
        return STARTING_CREDITS;
    }
}

function write(v) {
    try { localStorage.setItem(KEY, String(v)); } catch {}
    window.dispatchEvent(new CustomEvent('credits:changed', { detail: { credits: v } }));
}

/**
 * useCredits — subscribe to the global credit counter.
 * - credits: current balance
 * - low: true when at or below the LOW_THRESHOLD
 * - out: true when balance <= 0
 * - spend(cost = 1): decrements and returns true if allowed, false if out
 * - grant(amount): top up (e.g. after a purchase)
 */
export function useCredits() {
    const [credits, setCredits] = useState(read);

    useEffect(() => {
        const onChange = (e) => setCredits(e.detail?.credits ?? read());
        const onStorage = (e) => { if (e.key === KEY) setCredits(read()); };
        window.addEventListener('credits:changed', onChange);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener('credits:changed', onChange);
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    const spend = useCallback((cost = 1) => {
        const current = read();
        if (current <= 0) return false;
        write(Math.max(0, current - cost));
        return true;
    }, []);

    const grant = useCallback((amount) => {
        write(read() + amount);
    }, []);

    return {
        credits,
        low: credits <= LOW_THRESHOLD,
        out: credits <= 0,
        spend,
        grant,
    };
}

export const CREDITS_CONFIG = { STARTING_CREDITS, LOW_THRESHOLD };
